/**
 * API Client with JWT Authentication and Auto-Refresh
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_URL, ENDPOINTS } from './constants';
import { getFingerprint } from './fingerprint';
import { logger } from './logger';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let accessToken: string | null = null;
let refreshToken: string | null = null;
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

export function setTokens(access: string, refresh: string) {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem('accessToken', access);
  localStorage.setItem('refreshToken', refresh);
}

export function getAccessToken(): string | null {
  if (!accessToken) {
    accessToken = localStorage.getItem('accessToken');
  }
  return accessToken;
}

export function getRefreshToken(): string | null {
  if (!refreshToken) {
    refreshToken = localStorage.getItem('refreshToken');
  }
  return refreshToken;
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

// Request interceptor: attach JWT and device fingerprint
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Attach device fingerprint for security
    const fingerprint = await getFingerprint();
    config.headers['X-Device-Fingerprint'] = fingerprint;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 and auto-refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refresh = getRefreshToken();
      if (!refresh) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_URL}${ENDPOINTS.AUTH.REFRESH}`, {
          refreshToken: refresh,
        });

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
        setTokens(newAccessToken, newRefreshToken);

        processQueue(null);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error);
        isRefreshing = false;
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Auth API
 */
export const authApi = {
  /**
   * Send OTP to phone number
   */
  sendOTP: async (phone: string) => {
    const response = await api.post(ENDPOINTS.AUTH.SEND_OTP, { phone, role: 'vendor' });
    return response.data;
  },

  /**
   * Verify OTP
   */
  verifyOTP: async (phone: string, otp: string) => {
    const response = await api.post(ENDPOINTS.AUTH.VERIFY_OTP, { phone, otp });
    
    // Backend returns: { success: true, data: { access_token, user } }
    const { access_token, user } = response.data.data || response.data;
    
    // Use access token for both (backend doesn't provide refresh token yet)
    setTokens(access_token, access_token);
    return { user, accessToken: access_token, refreshToken: access_token };
  },

  /**
   * Logout
   */
  logout: async () => {
    try {
      await api.post(ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      logger.error('Logout failed', error as Error);
    } finally {
      clearTokens();
    }
  },
};

/**
 * Vendor API
 */
export const vendorApi = {
  /**
   * Get vendor profile
   */
  getProfile: async () => {
    const response = await api.get(ENDPOINTS.VENDOR.PROFILE);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get vendor stats
   */
  getStats: async () => {
    const response = await api.get(ENDPOINTS.VENDOR.STATS);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Update vendor status
   */
  updateStatus: async (status: 'online' | 'offline' | 'busy') => {
    const response = await api.post(ENDPOINTS.VENDOR.UPDATE_STATUS, { status });
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Send heartbeat (keeps vendor online)
   */
  sendHeartbeat: async () => {
    const response = await api.post('/api/vendor/heartbeat');
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get vendor payout batches (Sprint 4G)
   */
  getPayoutBatches: async () => {
    const response = await api.get('/api/payouts/vendor/batches');
    return response.data; // Returns { success, data: { batches, count } }
  },
};

/**
 * Orders API
 */
export const ordersApi = {
  /**
   * Get vendor orders
   */
  getOrders: async () => {
    const response = await api.get(ENDPOINTS.ORDERS.LIST);
    const orders = response.data.data;
    
    // Transform backend field names to match frontend Order interface
    return orders.map((order: any) => ({
      ...order,
      orderId: order.id, // Duplicate id as orderId for consistency
      totalAmount: parseFloat(order.total) || 0,
      customerId: order.customer_id,
      customerName: order.customer_name,
      customerPhone: order.customer_phone,
      deliveryAddress: order.delivery_address,
      createdAt: order.created_at,
      updatedAt: order.updated_at,
      paymentMethod: order.payment_method,
    }));
  },

  /**
   * Accept order
   */
  acceptOrder: async (orderId: string, estimatedReadyTime: number) => {
    const endpoint = ENDPOINTS.ORDERS.ACCEPT.replace(':id', orderId);
    const response = await api.post(endpoint, { prep_eta_minutes: estimatedReadyTime });
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Reject order
   */
  rejectOrder: async (orderId: string, reason: string) => {
    const endpoint = ENDPOINTS.ORDERS.REJECT.replace(':id', orderId);
    const response = await api.post(endpoint, { reason });
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Update order status
   */
  updateStatus: async (orderId: string, status: string) => {
    const endpoint = ENDPOINTS.ORDERS.UPDATE_STATUS.replace(':id', orderId);
    const response = await api.post(endpoint, { status });
    return response.data.data; // Extract data from ApiResponse
  },
};

/**
 * Products API
 */
export const productsApi = {
  /**
   * Get all products
   */
  getProducts: async () => {
    const response = await api.get(ENDPOINTS.PRODUCTS.LIST);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Create product
   */
  createProduct: async (product: {
    name: string;
    description?: string;
    category?: string;
    unit: string;
    price_today: string | number;
    image_url?: string;
    stock_status?: string;
  }) => {
    const response = await api.post(ENDPOINTS.PRODUCTS.CREATE, product);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Update product
   */
  updateProduct: async (productId: string, updates: {
    name?: string;
    description?: string;
    category?: string;
    unit?: string;
    price_today?: string | number;
    image_url?: string;
    stock_status?: string;
    primary_image_id?: string | null;
    gallery_image_ids?: string[];
  }) => {
    const endpoint = ENDPOINTS.PRODUCTS.UPDATE.replace(':id', productId);
    const response = await api.put(endpoint, updates);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Delete product (soft delete)
   */
  deleteProduct: async (productId: string) => {
    const endpoint = ENDPOINTS.PRODUCTS.DELETE.replace(':id', productId);
    const response = await api.delete(endpoint);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Toggle product availability
   */
  toggleProduct: async (productId: string) => {
    const endpoint = ENDPOINTS.PRODUCTS.TOGGLE.replace(':id', productId);
    const response = await api.patch(endpoint);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Update stock status
   */
  updateStock: async (productId: string, stock_status: string) => {
    const endpoint = ENDPOINTS.PRODUCTS.STOCK.replace(':id', productId);
    const response = await api.patch(endpoint, { stock_status });
    return response.data.data; // Extract data from ApiResponse
  },
};

/**
 * Media API (Sprint 4I)
 */
export const mediaApi = {
  /**
   * Upload an image file
   * Returns media with resolved URLs (sm/md/lg)
   */
  upload: async (file: File, productId?: string, setPrimary: boolean = false) => {
    const formData = new FormData();
    formData.append('file', file);
    if (productId) {
      formData.append('productId', productId);
      formData.append('setPrimary', setPrimary.toString());
    }
    
    const response = await api.post('/api/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000, // 30s for file uploads
    });
    
    return response.data.data;
  },
  
  /**
   * Delete a media file
   */
  delete: async (mediaId: string) => {
    const response = await api.delete(`/api/media/${mediaId}`);
    return response.data.data;
  },
  
  /**
   * Get media details
   */
  get: async (mediaId: string) => {
    const response = await api.get(`/api/media/${mediaId}`);
    return response.data.data;
  },
};

/**
 * Chat API
 */
export const chatApi = {
  /**
   * Create or get thread for an order
   */
  createOrGetThread: async (orderId: string) => {
    const endpoint = ENDPOINTS.CHAT.CREATE_THREAD.replace(':orderId', orderId);
    const response = await api.post(endpoint);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get chat messages for a thread
   */
  getMessages: async (threadId: string, limit = 50, cursor?: string) => {
    const endpoint = ENDPOINTS.CHAT.GET_MESSAGES.replace(':threadId', threadId);
    const response = await api.get(endpoint, { params: { limit, cursor } });
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Send message to a thread
   */
  sendMessage: async (threadId: string, message: string) => {
    const endpoint = ENDPOINTS.CHAT.SEND_MESSAGE.replace(':threadId', threadId);
    const response = await api.post(endpoint, { message });
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Send typing indicator
   */
  sendTyping: async (threadId: string, isTyping: boolean = true) => {
    const endpoint = ENDPOINTS.CHAT.TYPING.replace(':threadId', threadId);
    const response = await api.post(endpoint, { isTyping });
    return response.data.data; // Extract data from ApiResponse
  },
};

/**
 * Earnings API
 */
export const earningsApi = {
  /**
   * Get earnings summary (all time, today, this week, this month)
   */
  getSummary: async () => {
    const response = await api.get(ENDPOINTS.EARNINGS.SUMMARY);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get daily earnings (last 30 days)
   */
  getDaily: async () => {
    const response = await api.get(ENDPOINTS.EARNINGS.DAILY);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get weekly earnings (last 12 weeks)
   */
  getWeekly: async () => {
    const response = await api.get(ENDPOINTS.EARNINGS.WEEKLY);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get monthly earnings (last 12 months)
   */
  getMonthly: async () => {
    const response = await api.get(ENDPOINTS.EARNINGS.MONTHLY);
    return response.data.data; // Extract data from ApiResponse
  },
};

/**
 * Alerts API
 */
export const alertsApi = {
  /**
   * Get all unresolved alerts
   */
  getAlerts: async () => {
    const response = await api.get(ENDPOINTS.ALERTS.LIST);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Mark alert as read
   */
  markAsRead: async (alertId: string) => {
    const endpoint = ENDPOINTS.ALERTS.MARK_READ.replace(':id', alertId);
    const response = await api.post(endpoint);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get alert statistics
   */
  getStats: async () => {
    const response = await api.get(ENDPOINTS.ALERTS.STATS);
    return response.data.data; // Extract data from ApiResponse
  },
};

/**
 * Promos API
 */
export const promosApi = {
  /**
   * Get all active promos
   */
  getPromos: async () => {
    const response = await api.get(ENDPOINTS.PROMOS.LIST);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get promo details
   */
  getPromo: async (promoId: string) => {
    const endpoint = ENDPOINTS.PROMOS.DETAILS.replace(':id', promoId);
    const response = await api.get(endpoint);
    return response.data.data; // Extract data from ApiResponse
  },

  /**
   * Get promo statistics
   */
  getStats: async () => {
    const response = await api.get(ENDPOINTS.PROMOS.STATS);
    return response.data.data; // Extract data from ApiResponse
  },
};

export default api;

